import React, { Key, ReactNode, useMemo } from 'react';
import classnames from 'classnames';

import styles from './Grid.module.css';

export interface GridProps<T> {
    items: T[];
    renderItem: (item: T, index: number) => ReactNode;
    getKey: (item: T) => Key;
    separateBy?: (item: T, prevItem: T | null) => boolean;
    renderSeparator?: (item: T, prevItem: T | null) => ReactNode;
    className?: string;
    itemClassName?: string;
    separatorClassName?: string;
    children?: ReactNode;
    title?: ReactNode;
}

export function Grid<T>({
    items,
    renderItem,
    getKey,
    separateBy,
    renderSeparator,
    className,
    itemClassName = styles.item,
    separatorClassName = styles.separator,
    children,
    title,
}: GridProps<T>) {
    const renderedItems = useMemo(
        () =>
            items.reduce<ReactNode[]>((acc, item, index) => {
                const prevItem: T | null = index > 0 ? items[index - 1]! : null;

                if (separateBy?.(item, prevItem)) {
                    const prevKey = prevItem ? getKey(prevItem) : 'start';
                    const currentKey = getKey(item);

                    acc.push(
                        <div key={`sep-${prevKey}-${currentKey}`} className={separatorClassName}>
                            {renderSeparator?.(item, prevItem)}
                        </div>
                    );
                }

                acc.push(
                    <div key={getKey(item)} className={classnames(styles.item, itemClassName)}>
                        {renderItem(item, index)}
                    </div>
                );

                return acc;
            }, []),
        [items, getKey, renderItem, separateBy, renderSeparator, itemClassName, separatorClassName]
    );

    return (
        <div className={styles.root}>
            {title}
            <div className={classnames(styles.grid, className)}>
                {renderedItems}
                {children}
            </div>
        </div>
    );
}
